import React from 'react';
import { useAuth } from '../../../hooks/AuthProvider';
import { useLocations } from '../../../hooks/useLocations';

type NavigationItem = 'app-management' | 'permissions' | 'transactions' | 'clients' | 'vendors' | 'invoices' | 'expenses';

interface NavigationSidebarProps {
  selectedOrganizationId: string;
  activeItem: NavigationItem;
  onBackToOrganizations: () => void;
  onNavigateToApps: () => void;
  onNavigateToPermissions?: () => void;
  onNavigateToTransactions?: () => void;
  onNavigateToClients?: () => void;
  onNavigateToVendors?: () => void;
  onNavigateToInvoices?: () => void;
  onNavigateToExpenses?: () => void;
}

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({
  selectedOrganizationId,
  activeItem,
  onBackToOrganizations,
  onNavigateToApps,
  onNavigateToPermissions,
  onNavigateToTransactions,
  onNavigateToClients,
  onNavigateToVendors,
  onNavigateToInvoices,
  onNavigateToExpenses
}) => {
  const { userPermissions, hasPermission } = useAuth();
  const { getOrganizationName } = useLocations();

  // Get organization name using the hook
  const getDisplayOrganizationName = (): string => {
    const orgName = getOrganizationName(selectedOrganizationId);
    return formatOrganizationName(orgName);
  };

  // Helper function to format organization names nicely
  const formatOrganizationName = (name: string): string => {
    if (name === 'DINEUKFINALMILE') return 'Dine UK Final Mile';
    if (name === 'DINEUSA99') return 'Dine USA 99';
    
    return name
      .toLowerCase()
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, str => str.toUpperCase());
  };

  // Check if user has admin access to this organization
  const hasAdminAccess = (): boolean => {
    return userPermissions.some(permission => 
      permission === `organizations_${selectedOrganizationId}_admin`
    );
  };

  // Check if user has any access to this organization (read/write/admin)
  const hasAnyAccess = (): boolean => {
    return userPermissions.some(permission => 
      permission.startsWith(`organizations_${selectedOrganizationId}_`)
    );
  };

  // Get organizations the user has access to
  const getUserOrganizations = (): string[] => {
    const organizations = new Set<string>();
    
    userPermissions.forEach(permission => {
      if (permission.startsWith('organizations_')) {
        const parts = permission.split('_');
        if (parts.length >= 3) {
          const orgId = parts[1];
          organizations.add(orgId);
        }
      }
    });

    return Array.from(organizations);
  };

  // Check if user has access to multiple organizations
  const hasMultipleOrganizations = (): boolean => {
    return getUserOrganizations().length > 1;
  };

  return (
    <div className="applist-sidebar">
      <div className="applist-sidebar-header">
        <h3 className="applist-sidebar-title">Administration</h3>
        <p className="applist-sidebar-subtitle">Manage your organization</p>
      </div>
      
      <div className="applist-sidebar-navigation">
        <div className="applist-nav-section">
          <div className="applist-nav-section-title">Navigation</div>
          
          {/* Only show "All Organizations" if user has access to multiple organizations */}
          {hasMultipleOrganizations() && (
            <button 
              type="button" 
              className="applist-nav-item"
              onClick={onBackToOrganizations}
            >
              <span className="applist-nav-icon">←</span>
              All Organizations
            </button>
          )}
          
          <button 
            type="button" 
            className={`applist-nav-item ${activeItem === 'app-management' ? 'applist-nav-item-active' : ''}`}
            onClick={onNavigateToApps}
          >
            <span className="applist-nav-icon">📱</span>
            App Management
          </button>

          {/* Only show permissions if user has admin access and callback is provided */}
          {hasAdminAccess() && onNavigateToPermissions && (
            <button 
              type="button" 
              className={`applist-nav-item ${activeItem === 'permissions' ? 'applist-nav-item-active' : ''}`}
              onClick={onNavigateToPermissions}
            >
              <span className="applist-nav-icon">👥</span>
              Permissions
            </button>
          )}
        </div>

        {/* JOTLU Track Section */}
        {hasAnyAccess() && (
          <div className="applist-nav-section">
            <div className="applist-nav-section-title">JOTLU Track</div>
            
            {onNavigateToTransactions && (
              <button 
                type="button" 
                className={`applist-nav-item ${activeItem === 'transactions' ? 'applist-nav-item-active' : ''}`}
                onClick={onNavigateToTransactions}
              >
                <span className="applist-nav-icon">💰</span>
                Transactions
              </button>
            )}

            {onNavigateToClients && (
              <button 
                type="button" 
                className={`applist-nav-item ${activeItem === 'clients' ? 'applist-nav-item-active' : ''}`}
                onClick={onNavigateToClients}
              >
                <span className="applist-nav-icon">👤</span>
                Clients
              </button>
            )}

            {onNavigateToVendors && (
              <button 
                type="button" 
                className={`applist-nav-item ${activeItem === 'vendors' ? 'applist-nav-item-active' : ''}`}
                onClick={onNavigateToVendors}
              >
                <span className="applist-nav-icon">🏢</span>
                Vendors
              </button>
            )}

            {onNavigateToInvoices && (
              <button 
                type="button" 
                className={`applist-nav-item ${activeItem === 'invoices' ? 'applist-nav-item-active' : ''}`}
                onClick={onNavigateToInvoices}
              >
                <span className="applist-nav-icon">📄</span>
                Invoices
              </button>
            )}

            {onNavigateToExpenses && (
              <button 
                type="button" 
                className={`applist-nav-item ${activeItem === 'expenses' ? 'applist-nav-item-active' : ''}`}
                onClick={onNavigateToExpenses}
              >
                <span className="applist-nav-icon">🧾</span>
                Expenses
              </button>
            )}
          </div>
        )}
      </div>
      
      <div className="applist-organization-info">
        <h4>{getDisplayOrganizationName()}</h4>
        <p>Current Organization</p>
        
        {/* Only show "Change Organization" if user has access to multiple organizations */}
        {hasMultipleOrganizations() && (
          <button 
            type="button" 
            className="applist-change-org-btn"
            onClick={onBackToOrganizations}
          >
            Change Organization
          </button>
        )}
      </div>
    </div>
  );
};

export default NavigationSidebar;